/**
 * Transfer Balance Cap — the lifetime limit (~$2m per person) on money moved into retirement-phase
 * pensions. A strategy that "commences an account-based pension" for a person with no headroom is
 * recommending something the law does not allow, so the engine SPLITS at the cap: the headroom goes
 * into the pension, the excess into accumulation super (clearly named), and the ranking prices each
 * part at its own tax treatment.
 *
 * What counts against a person's cap is the amounts TRANSFERRED into retirement phase over their
 * lifetime — not today's balance (growth doesn't consume cap; drawdowns don't restore it). The tool
 * therefore works from, in order:
 *   1. the person's RECORDED lifetime transfer amount (`client/partner.transferBalanceUsed`, from
 *      the ATO's "transfer balance account" figure in myGov), asked just-in-time only when the
 *      screen below trips — never as a routine fact-find question; else
 *   2. an ESTIMATE: the person's current retirement-phase pension balances. Stated wherever it is
 *      used, and in the SOA assumptions.
 *
 * DOCUMENTED LIMITATION (adviser-accepted 2026-08-20, disclosed in the SOA assumptions whenever a
 * strategy commences a pension): the estimate can UNDERSTATE cap usage for a person who moved a
 * large sum into a pension years ago and has since drawn it down — their history is invisible to a
 * balance-based estimate. The just-in-time question exists precisely to let an adviser correct
 * this; the screen at $1.6m (the lowest possible personal cap) keeps the question away from every
 * file it cannot affect.
 */
import { GENERAL_TRANSFER_BALANCE_CAP, TRANSFER_BALANCE_CAP_SCREEN, DOWNSIZER_CONTRIBUTION_CAP } from './constants/ratesAndThresholds.ts';
import type { FactFind } from './factFindTypes.ts';

export type TbcOwnership = 'client' | 'partner';

const num = (v: unknown): number => Math.max(0, parseFloat(String(v ?? '')) || 0);

/** The person's current retirement-phase pension balances (their ABPs; joint split half each). */
export function currentPensionBalancesOf(factFind: FactFind | null | undefined, who: TbcOwnership): number {
    return (factFind?.assets?.accountBasedPensions || []).reduce((sum, abp) => {
        const value = num(abp?.value);
        if (abp?.ownership === who) return sum + value;
        if (abp?.ownership === 'joint') return sum + value / 2;
        return sum;
    }, 0);
}

/** Whether the person has RECORDED their lifetime transfer amount (vs the tool estimating it). */
export function hasRecordedTransferBalance(factFind: FactFind | null | undefined, who: TbcOwnership): boolean {
    const person = who === 'partner' ? factFind?.partner : factFind?.client;
    const raw = (person as { transferBalanceUsed?: unknown } | undefined)?.transferBalanceUsed;
    return raw !== undefined && raw !== null && raw !== '' && Number.isFinite(parseFloat(String(raw)));
}

/** Lifetime cap usage: the recorded ATO figure, or the balance-based estimate (see header). */
export function transferBalanceUsedOf(factFind: FactFind | null | undefined, who: TbcOwnership): number {
    if (hasRecordedTransferBalance(factFind, who)) {
        const person = who === 'partner' ? factFind?.partner : factFind?.client;
        return num((person as { transferBalanceUsed?: unknown }).transferBalanceUsed);
    }
    return currentPensionBalancesOf(factFind, who);
}

/** How much more this person can move into a retirement-phase pension. */
export function pensionHeadroomOf(factFind: FactFind | null | undefined, who: TbcOwnership): number {
    return Math.max(0, GENERAL_TRANSFER_BALANCE_CAP - transferBalanceUsedOf(factFind, who));
}

/**
 * Should the tool ASK this person about their cap usage before modelling? Only when the answer
 * could change something: no recorded figure, and (current pensions + the largest commencement a
 * downsizer strategy could propose) reaches the $1.6m screen — below that, no personal cap can
 * bind, so no question is ever shown.
 */
export function tbcQuestionNeededFor(factFind: FactFind | null | undefined, who: TbcOwnership): boolean {
    if (hasRecordedTransferBalance(factFind, who)) return false;
    return currentPensionBalancesOf(factFind, who) + DOWNSIZER_CONTRIBUTION_CAP >= TRANSFER_BALANCE_CAP_SCREEN;
}
